
import { ActionTree, GetterTree, Module, MutationTree } from "vuex";
import { IHolidayState } from "./types";
import { IIDAStore } from "../types";
import { institutionService } from "../../services/institution.service";
import { Holiday } from "../../models/holiday";
import { endOfDay, getYear, isSameDay, startOfDay } from "date-fns";
import store from "../../store";

export const getInitialState = (): IHolidayState => {
  return {
    holidays: []
  };
};

const namespaced = true;

export const getters: GetterTree<IHolidayState, IIDAStore> = {
  GetHolidays: (state) => () => {
    return state.holidays;
  },
  GetHolidayByDate: (state) => (date: Date) => {
    return state.holidays[getYear(date)].find((holiday: Holiday) => isSameDay(holiday.date, date));
  }
};

export const actions: ActionTree<IHolidayState, IIDAStore> = {
  reset({ commit }) {
    commit("reset");
  },
  fetchHolidays({ commit, state }, year: number): Promise<Holiday[]> {
    return new Promise((resolve, reject) => {
      if (state.holidays[year] === undefined) {
        try {
          institutionService.fetchHolidays(year).then((holidays: Holiday[]) => {
            commit("updateHolidays", { holidays, year });
            return resolve(holidays);
          });
        } catch (error) {
          return reject(error);
        }
      } else {
        resolve(state.holidays[year]);
      }
    });
  },
  fetchHolidaysByDates(
    { dispatch },
    payload: { fromdate: Date; todate: Date | undefined }
  ): Promise<Holiday[]> {
    return new Promise((resolve, reject) => {
      if (payload.todate !== undefined && getYear(payload.fromdate) === getYear(payload.todate)) {
        dispatch("fetchHolidays", getYear(payload.fromdate))
          .then((yearHolidays: Holiday[]) => {
            resolve(
              yearHolidays.filter(
                (h: Holiday) =>
                  h.date >= startOfDay(payload.fromdate) &&
                  (payload.todate == undefined || h.date <= endOfDay(payload.todate))
              )
            );
          })
          .catch((error) => {
            reject(error);
          });
      } else {
        const fromYear = getYear(payload.fromdate);
        const toYear = getYear(payload.todate ?? new Date());
        const years = Array.from({ length: toYear - fromYear + 1 }, (_, i) => fromYear + i);

        Promise.all(years.map((year) => dispatch("fetchHolidays", year))).then(
          (yearHolidaysArray) => {
            const allHolidays = yearHolidaysArray.flat() as Holiday[];

            resolve(
              allHolidays.filter(
                (h: Holiday) =>
                  h.date >= payload.fromdate &&
                  (payload.todate == undefined || h.date <= endOfDay(payload.todate))
              )
            );
          }
        );
      }
    });
  }
};

export const mutations: MutationTree<IHolidayState> = {
  reset(state: IHolidayState) {
    Object.assign(state, getInitialState());
  },
  updateHolidays(state: IHolidayState, payload: { holidays: Holiday[]; year: number }) {
    state.holidays[payload.year] = payload.holidays;
  }
};

export const holidayStoreModule: Module<IHolidayState, IIDAStore> = {
  namespaced: namespaced,
  state: getInitialState(),
  getters: getters,
  actions: actions,
  mutations: mutations
};

export namespace StoreHoliday {

  export function fetchHolidaysByDates(
    firstDay: Date,
    lastDay: Date | undefined
  ): Promise<Holiday[]> {
    return store.dispatch("holidays/fetchHolidaysByDates", {
      fromdate: firstDay,
      todate: lastDay
    });
  }

  export function isHoliday(date: Date): boolean {
    return !!store.getters["holidays/GetHolidayByDate"](date);
  }

  export function getHolidaysInDates(dates: Date[]): Holiday[] {
    return dates
      .map((d) => store.getters["holidays/GetHolidayByDate"](d) as Holiday | undefined)
      .filter((h): h is Holiday => !!h);
  }

  export function filterHolidays(dates: Date[]): Date[] {
    return dates.filter(
      (d) => !store.getters["holidays/GetHolidayByDate"](d)
    );
  }

  
}
